import React, {useEffect, useState, useCallback, Dispatch, SetStateAction} from 'react'
import {useBasket, useWishlist, useWishlistActions, useAnalytics} from '_customHooks'
import {useProductTileAnalyticsContext} from '_context'
import {ProductTileOverlayWishlist} from './overlay/variants/wishlist'
import {ProductTileOverlay} from './overlay'
import {getProductImpressionAnalytics, getProductAnalyticsInfo} from '_analytics/utils/analyticsUtils'
import type {ProductType} from '_types'

export interface ProductOverlayWrapperProps {
    product: ProductType
    variant?: 'wishlist' | 'shoptheimage'
    selectedVariant: string | null
    selectedSize: string | null
    setSelectedSize: Dispatch<SetStateAction<string | null>>
    setSelectedVariant: Dispatch<SetStateAction<string | null>>
    toggleSizeSelector: () => void
    isInStock: boolean
    productLink: string
    analyticsList?: string
    idx: number
    tileAssetSize: number
    isFullWidth?: boolean
    onAddProductToCart?: () => void
    onUpdateItemSize?: (variantId: string) => void
}

/**
 * @name ProductOverlayWrapper
 *
 * @component
 *
 * @description
 * Handles the rendering of the overlay for product tile.
 *
 * @param {ProductOverlayWrapperProps} props
 *
 * @returns {React.FC<ProductOverlayWrapperProps>} Rendered `ProductOverlayWrapper` component.
 */
export const ProductOverlayWrapper: React.FC<ProductOverlayWrapperProps> = ({
    product,
    variant,
    selectedSize,
    setSelectedSize,
    setSelectedVariant,
    toggleSizeSelector,
    isInStock,
    productLink,
    analyticsList,
    idx,
    tileAssetSize,
    isFullWidth,
    onAddProductToCart,
    onUpdateItemSize,
}) => {
    const [sizeErrorVisible, setSizeErrorVisible] = useState<boolean>(false)

    const {addItemToBasket} = useBasket()
    const {wishlistType} = useWishlist()
    const {removeItemFromWishlist} = useWishlistActions()
    const {analyticsEventAddToCart} = useAnalytics()
    const productTileAnalyticsContext = useProductTileAnalyticsContext()
    const {productCurrency, activeVariation, productPriceNumber} = getProductAnalyticsInfo(product)

    const addProductToCart = useCallback(() => {
        if (selectedSize) {
            addItemToBasket({
                productId: selectedSize,
                quantity: 1,
                price: productPriceNumber,
            })
                .then(async () => {
                    const analyticsProductObject = getProductImpressionAnalytics({
                        product,
                        idx,
                        tileAssetSize,
                        isFullWidth,
                        selectedSize,
                        analyticsList,
                        flow: 'addToCart',
                        page: variant || 'plp',
                        productSortingEngine: productTileAnalyticsContext?.productSortingEngine,
                        productSortingType: productTileAnalyticsContext?.productSortingType,
                    })

                    const {list, ...analyticsProductWithoutList} = analyticsProductObject
                    delete analyticsProductWithoutList.dimension14

                    toggleSizeSelector()
                    analyticsEventAddToCart({
                        currencyCode: productCurrency,
                        list,
                        product: analyticsProductWithoutList,
                    })

                    if (variant === 'wishlist') {
                        onAddProductToCart?.()

                        if (wishlistType === 'private') {
                            await removeItemFromWishlist(product.id)
                        }
                    }
                    // Dynamic Yield add to cart Tracking
                    if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function') {
                        const productVariationsGroup = product?.c_variationGroupsData || product?.variationGroups
                        const variations = productVariationsGroup?.find((vg) => vg.color.code === product.c_color).variants
                        const selectedVariant = variations?.find((variant) => variant.ID === selectedSize)
                        const productPrice = selectedVariant?.pricing.price.value
                        try {
                            window.DY.API('event', {
                                name: 'Add to Cart',
                                properties: {
                                    dyType: 'add-to-cart-v1',
                                    value: productPrice,
                                    currency: productCurrency,
                                    productId: selectedSize,
                                    quantity: 1,
                                    size: analyticsProductObject.dimension21,
                                },
                            })
                        } catch (error) {
                            console.error('[DY Tracking] Errore nel tracking add to cart:', error)
                        }
                    }
                })
                // .then(() => previewMinicart())
                .catch((error) => {
                    console.error('Failed to add item to basket:', error)
                })
        } else {
            setSizeErrorVisible(true)
        }
    }, [
        selectedSize,
        addItemToBasket,
        removeItemFromWishlist,
        product,
        analyticsList,
        idx,
        tileAssetSize,
        isFullWidth,
        wishlistType,
        onAddProductToCart,
        analyticsEventAddToCart,
        productCurrency,
        productTileAnalyticsContext?.productSortingEngine,
        productTileAnalyticsContext?.productSortingType,
        toggleSizeSelector,
        variant,
    ])

    useEffect(() => {
        if (variant === 'wishlist' && selectedSize) {
            onUpdateItemSize?.(selectedSize)
        }
    }, [selectedSize])

    return variant === 'wishlist' ? (
        <ProductTileOverlayWishlist
            product={product}
            productLink={productLink}
            isInStock={isInStock}
            selectedSize={selectedSize}
            selectedColor={activeVariation?.color?.label}
            sizeErrorVisible={sizeErrorVisible}
            setSizeErrorVisible={setSizeErrorVisible}
            setSelectedSize={setSelectedSize}
            addProductToCart={addProductToCart}
        />
    ) : (
        <ProductTileOverlay
            product={product}
            selectedSize={selectedSize}
            sizeErrorVisible={sizeErrorVisible}
            setSizeErrorVisible={setSizeErrorVisible}
            setSelectedSize={setSelectedSize}
            setSelectedVariant={setSelectedVariant}
            addProductToCart={addProductToCart}
        />
    )
}
